import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

import BarcodeInput from 'components/field/BarcodeInput';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useMediaQuery from 'hooks/useMediaQuery';

import { SOUND } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';

import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';

import SerialNumberForm from './SerialNumberForm';

const colSpan = { md: 6 };

const OrderDetails = ({ nextStep, isSerialNumberValid }) => {
	const {
		watch,
		setValue,
		formState: { isLoading: isFormLoading, isSubmitting }
	} = useFormContext();

	const isLoading = isSubmitting || isFormLoading;

	const { showError } = useAlert();

	const globalValue = useRef('');

	const shipmentNumber = watch('shipmentNumber');

	useEffect(() => {
		if (watch('products')?.every(({ checked }) => checked) && isSerialNumberValid) {
			nextStep();
		}
	}, [watch()]);

	useEffect(() => {
		document.onkeydown = (e) => {
			if ((e.which < 65 || e.which > 122) && (e.which < 48 || e.which > 57)) {
				if (e.key === 'Enter') {
					onBarcodeEnter(globalValue.current);
					globalValue.current = '';
				} else if (e.key === '-' || e.key === '/') {
					globalValue.current = `${globalValue.current}${e.key}`;
				}
				return;
			}
			globalValue.current = globalValue.current + e.key;
		};
		return () => {
			document.onkeydown = null;
		};
	}, []);

	const onSuccessItemSerialNumber = (serialNumbers, item) => {
		setValue(`products[${item?.index}].serialNumber`, serialNumbers?.join?.(','), {
			shouldValidate: true,
			shouldDirty: true
		});
	};

	const openSerialNumberForm = (item, index) => {
		NiceModal.show(SerialNumberForm, {
			id: item?.ID,
			serialNumberList: item?.serialNumber?.split?.(',') || [],
			isRequired: item?.serialRequirement === 'Required',
			hasUnique: item?.serialType === 'unique',
			quantity: item?.quantity,
			onSuccess: (serialNumber) => onSuccessItemSerialNumber(serialNumber, { ...item, index })
		});
	};

	const isMobile = useMediaQuery(Breakpoints.mobile);
	const productTableColumns = useMemo(
		() => [
			{
				header: 'nameAndSku',
				renderCell: ({ row }) => <ProductImage data={row.original} sku={row.original.SKU} />
			},
			{
				id: 'barcode',
				header: 'barcode',
				showOnMobile: true,
				size: 50,
				renderCell: ({ row: { index } }) => <Typography>{watch(`products[${index}].barcode`)}</Typography>
			},
			{
				id: 'quantity',
				header: 'quantity',
				size: isMobile ? 20 : 25,
				renderCell: ({ row: { index } }) => <Typography>{watch(`products[${index}].quantity`)}</Typography>,
				showOnMobile: true
			},
			{
				id: 'pickedQty',
				header: 'pickedQty',
				size: isMobile ? 20 : 25,
				renderCell: ({ row: { index } }) => <Typography>{watch(`products[${index}].pickedQTY`)}</Typography>,
				showOnMobile: true
			},
			{
				id: 'checked',
				header: '',
				size: isMobile ? 10 : 5,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => {
					const checked = watch(`products[${index}].checked`);
					return (
						<Button
							icon={checked ? faCheck : faXmark}
							color={checked ? 'var(--green)' : 'var(--red)'}
							iconButton
							size="small"
						/>
					);
				}
			},
			{
				id: 'serialNumber',
				header: 'serialNumber',
				size: isMobile ? 30 : 35,
				renderCell: ({ row: { original, index } }) =>
					['Required', 'Optional'].includes(original?.serialRequirement) && (
						<Button
							size="small"
							label="serialNumber"
							onClick={() => openSerialNumberForm(original, index)}
						/>
					),
				showOnMobile: true
			}
		],
		[]
	);

	const onBarcodeEnter = (value) => {
		const productIndexes = [];
		// If with the same barcode there are more than one product, we will take the first one that is not checked
		AppLogger.log('1: onBarcodeEnter with value ', value);
		watch('products').forEach((product, index) => {
			if (product?.barcodes?.find((e) => e === value) || product.SKU === value) productIndexes.push(index);
		});
		AppLogger.log('2: products, productIndexes', JSON.stringify(watch('products')), productIndexes);

		if (!productIndexes.length) {
			showError({ message: 'errorMsg.barcodeNotFound' });
			const errorSound = new Audio(SOUND.BARCODE_ERROR);
			errorSound.play();
			AppLogger.log('3: barcode not found');
			return;
		}

		const productIndex = productIndexes.find((index) => !watch(`products[${index}].checked`));
		const productConst = `products[${productIndex}]`;

		AppLogger.log('4: productIndex, productConst', productIndex, productConst);

		const quantity = watch(`${productConst}.quantity`) ?? 0;
		const pickedQTY = watch(`${productConst}.pickedQTY`) || 0;

		AppLogger.log('5: quantity, pickedQTY', quantity, pickedQTY);

		if (productIndex >= 0 && quantity > pickedQTY) {
			AppLogger.log('6: success barcode first if');
			setValue(`${productConst}.pickedQTY`, pickedQTY ? pickedQTY + 1 : 1, { shouldValidate: true });
			const successSound = new Audio(SOUND.BARCODE_SUCCESS);
			successSound.play();
		} else {
			AppLogger.log('6: failed barcode first if', productIndex, quantity, pickedQTY);
		}
		if (pickedQTY + 1 === quantity) {
			setValue(`${productConst}.checked`, true, { shouldValidate: true });
			AppLogger.log('7: success barcode second if');
		} else {
			AppLogger.log('7: failed barcode second if', pickedQTY, quantity);
		}
		if (pickedQTY + 1 > quantity) {
			AppLogger.log('8: success barcode third if');
			const errorSound = new Audio(SOUND.BARCODE_ERROR);
			errorSound.play();
			showError({ message: 'pickedQTY is greater than quantity' });
		} else {
			AppLogger.log('8: failed barcode third if', pickedQTY, quantity);
		}
	};

	return (
		<>
			<FormItem label="orderID" colSpan={colSpan} loading={isLoading}>
				<StringInput name="orderIDShowed" disabled />
			</FormItem>
			<FormItem label="orderDate" colSpan={colSpan} loading={isLoading}>
				<DateInput name="orderDate" value={watch('orderDate')} disabled enableTime />
			</FormItem>
			<FormItem label="shippingDescription">
				<StringInput name="shippingDescription" disabled loading={isLoading} />
			</FormItem>
			{shipmentNumber && (
				<FormItem label="shipmentNumber" colSpan={colSpan} required={!shipmentNumber} loading={isLoading}>
					<StringInput name="shipmentNumber" disabled />
				</FormItem>
			)}
			<FormItem label="barcodeOrSKU" colSpan={{ md: 12 }} loading={isLoading}>
				<BarcodeInput onChange={onBarcodeEnter} />
			</FormItem>
			<Table columns={productTableColumns} data={watch('products')} loading={isLoading} />
		</>
	);
};
export default OrderDetails;
